"use client";

import {
  Auth0Client,
  User,
  GetTokenSilentlyOptions,
} from "@auth0/auth0-spa-js";
import { AUTH0_CONFIG, isAuthPath, getLoginUrl } from "./auth0-config";

// Client-side only
let auth0Client: Auth0Client | null = null;

// Initialize Auth0 client (client-side only)
export const getAuth0Client = (): Auth0Client => {
  if (typeof window === "undefined") {
    throw new Error("Auth0 client can only be used in browser environment");
  }

  if (!auth0Client) {
    auth0Client = new Auth0Client(AUTH0_CONFIG);
  }

  return auth0Client;
};

// Login with Auth0 Universal Login (with PKCE)
export const login = async (returnTo?: string) => {
  const auth0 = getAuth0Client();
  await auth0.loginWithRedirect({
    authorizationParams: {
      ...AUTH0_CONFIG.authorizationParams,
      // Add returnTo to appState for post-login redirect
      appState: returnTo ? { returnTo } : undefined,
    },
  });
};

// Handle the authentication callback
export const handleAuthCallback = async () => {
  const auth0 = getAuth0Client();

  try {
    // Handle the redirect callback
    const result = await auth0.handleRedirectCallback();
    // Get the authentication result
    const isAuthenticated = await auth0.isAuthenticated();

    if (isAuthenticated) {
      const user = await auth0.getUser();
      const token = await auth0.getTokenSilently();

      return {
        user,
        token,
        returnTo: result.appState?.returnTo,
      };
    }

    return null;
  } catch (error) {
    console.error("Error handling redirect callback:", error);
    throw error;
  }
};

// Get user profile
export const getUserProfile = async (): Promise<User | undefined> => {
  const auth0 = getAuth0Client();
  return auth0.getUser();
};

// Get access token (for API calls)
export const getAccessToken = async (
  options?: GetTokenSilentlyOptions
): Promise<string> => {
  const auth0 = getAuth0Client();

  try {
    // Try to get a token silently first
    const token = await auth0.getTokenSilently(options);

    // Check if token is valid JSON
    try {
      // Split the token to get the payload part
      const parts = token.split(".");
      if (parts.length !== 3) {
        console.warn("[Auth0] Invalid token structure");
        return token; // Return as is if structure doesn't look right
      }

      // Base64 decode and parse as JSON
      const payload = JSON.parse(atob(parts[1]));
      const expiresIn = payload.exp * 1000 - Date.now(); // Convert to ms

      if (expiresIn < 0) {
        console.warn("[Auth0] Token has expired");
      } else {
        console.log(
          `[Auth0] Token valid for ${Math.floor(expiresIn / 1000)} seconds`
        );
      }
    } catch (e) {
      console.error("[Auth0] Error parsing token", e);
    }

    return token;
  } catch (error) {
    console.error("[Auth0] Failed to get token silently:", error);
    throw error;
  }
};

// Check if user is authenticated
export const isAuthenticated = async (): Promise<boolean> => {
  const auth0 = getAuth0Client();
  return auth0.isAuthenticated();
};

// Logout
export const logout = async () => {
  const auth0 = getAuth0Client();
  await auth0.logout({
    logoutParams: {
      returnTo: typeof window !== "undefined" ? window.location.origin : "",
    },
  });
};

/**
 * Debug token format and validity
 */
export async function debugAuthToken(): Promise<{
  isAuthenticated: boolean;
  hasToken: boolean;
  token?: string;
  tokenPreview?: string;
  tokenHeader?: Record<string, unknown>;
  tokenError?: string;
  tokenLength?: number;
  hasAuthHeader?: boolean;
}> {
  try {
    const auth0 = getAuth0Client();
    const isAuthenticated = await auth0.isAuthenticated();

    if (!isAuthenticated) {
      return {
        isAuthenticated: false,
        hasToken: false,
        tokenError: "Not authenticated",
      };
    }

    try {
      const token = await auth0.getTokenSilently();
      const hasToken = !!token;

      // Get token preview (first 10 chars + ... + last 10 chars)
      const tokenPreview =
        token.length > 20
          ? `${token.substring(0, 10)}...${token.substring(token.length - 10)}`
          : token;

      let tokenHeader = null;
      let tokenError = undefined;

      try {
        // Try to parse token header
        if (token.includes(".")) {
          const headerPart = token.split(".")[0];
          const decodedHeader = atob(headerPart);
          tokenHeader = JSON.parse(decodedHeader);
        }
      } catch (e) {
        tokenError = `Header parsing error: ${
          e instanceof Error ? e.message : String(e)
        }`;
      }

      // Check if auth header would be properly formatted
      const hasAuthHeader = token ? true : false;

      return {
        isAuthenticated,
        hasToken,
        token: token,
        tokenPreview,
        tokenHeader,
        tokenError,
        tokenLength: token.length,
        hasAuthHeader,
      };
    } catch (e) {
      return {
        isAuthenticated,
        hasToken: false,
        tokenError: e instanceof Error ? e.message : String(e),
      };
    }
  } catch (error) {
    return {
      isAuthenticated: false,
      hasToken: false,
      tokenError: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Check if user is authenticated and redirect to login if not
 * Used to protect routes that require authentication
 */
export async function checkAuthAndRedirect(): Promise<boolean> {
  try {
    // If we're on an auth path, don't check authentication to avoid loops
    if (typeof window !== "undefined" && isAuthPath()) {
      return false;
    }

    const auth0 = getAuth0Client();
    const isAuthenticated = await auth0.isAuthenticated();

    if (!isAuthenticated) {
      console.log("[Auth0] User not authenticated, redirecting to login");
      window.location.href = getLoginUrl(window.location.pathname);
      return false;
    }

    return true;
  } catch (error) {
    console.error("[Auth0] Auth check failed:", error);
    return false;
  }
}
